import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import CtaBand from "@/components/CtaBand";
import { Reveal } from "@/components/Reveal";

const CAPABILITIES = [
  {
    n: "01",
    title: "Product Discovery & Strategy",
    tagline: "Find what needs to change before committing to change it.",
    items: ["Stakeholder interviews", "Current-state analysis", "Root-cause analysis", "Opportunity prioritization"],
  },
  {
    n: "02",
    title: "Requirements & Workflow Design",
    tagline: "Turn ambiguity into buildable, testable specification.",
    items: ["Business & functional requirements", "Epics, user stories, acceptance criteria", "Future-state workflow design", "Prioritization"],
  },
  {
    n: "03",
    title: "Rapid Prototyping",
    tagline: "Give stakeholders something to react to in days, not months.",
    items: ["Clickable prototypes", "Proofs-of-concept", "Process simulations", "“Is this what you mean?” validation"],
  },
  {
    n: "04",
    title: "Technology & Vendor Evaluation",
    tagline: "Choose solutions against requirements, not demos.",
    items: ["Solution option analysis", "Vendor evaluation", "Solicitation & RFP requirements support", "Fit-gap assessment"],
  },
  {
    n: "05",
    title: "Product Ownership & Implementation Governance",
    tagline: "Keep delivery aligned to intent.",
    items: ["Backlog leadership", "Implementation oversight", "Dependency & risk management", "Vendor coordination", "Executive reporting"],
  },
  {
    n: "06",
    title: "Transformation Program Leadership",
    tagline: "Hold the whole effort together.",
    items: ["Program structure", "Decision governance", "Adoption & change planning", "Outcome accountability"],
  },
];

const AI_ITEMS = [
  "Identify where AI actually adds value",
  "Validate the use case quickly",
  "Define requirements and controls",
  "Move from experiment to implementation",
];

const ENVIRONMENTS = [
  { name: "People", desc: "The roles, incentives, and judgment inside the operation." },
  { name: "Processes", desc: "Workflows, handoffs, and exception paths as they actually run." },
  { name: "Policies", desc: "Rules as written — and as interpreted in practice." },
  { name: "Data", desc: "What the system believes to be true: freshness, lineage, quality." },
  { name: "Integrations", desc: "Where meaning moves — or is lost — between systems." },
  { name: "Software systems", desc: "Applications, platforms, and their configured behavior." },
];

export default function CapabilitiesPage() {
  return (
    <>
      <Seo
        title="Capabilities — Improve Systems"
        siteName="Improve Systems"
        description="Product discovery & strategy, requirements & workflow design, rapid prototyping, technology & vendor evaluation, product ownership & implementation governance, and transformation program leadership."
      />
      <PageHero
        eyebrow="CAPABILITIES"
        title="Six capabilities. One job: keep modernization connected to outcomes."
        lede="We are not an AI consultancy and not a software development shop. Improve Systems is a product and transformation advisory — we sit between the business problem and the technology implementation, and make sure the two stay connected."
        testid="capabilities-hero"
      />

      <section data-testid="capability-groups" className="border-b border-line bg-white">
        <div className="mx-auto max-w-7xl px-6 py-20 sm:py-28">
          <div className="grid gap-px border border-line bg-line md:grid-cols-2 lg:grid-cols-3">
            {CAPABILITIES.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.07} className="h-full">
                <article
                  data-testid={`capability-card-${i + 1}`}
                  className="group flex h-full flex-col bg-white p-8 transition-colors duration-300 hover:bg-canvas"
                >
                  <span className="font-mono text-[11px] text-slate-400">{c.n}</span>
                  <h2 className="mt-5 font-serif text-2xl leading-snug text-ink">{c.title}</h2>
                  <p className="mt-2 font-serif text-base italic text-slate-600">{c.tagline}</p>
                  <ul className="mt-7 border-t border-line">
                    {c.items.map((item) => (
                      <li
                        key={item}
                        className="flex items-center gap-3 border-b border-line py-2.5 text-[13px] text-slate-600"
                      >
                        <span className="block h-1 w-1 shrink-0 bg-slate-300" aria-hidden="true" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="ai-capability" className="border-b border-line bg-navy text-white">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-20 sm:py-24 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="mb-6 flex items-center gap-3 font-mono text-[11px] tracking-[0.3em] text-slate-400">
                <span className="h-px w-10 bg-slate-600" aria-hidden="true" />
                UNDERNEATH IT ALL — A TOOL, NOT OUR IDENTITY
              </p>
              <h2 className="font-serif text-3xl leading-tight tracking-tight sm:text-4xl">
                AI Strategy & Prototyping.
              </h2>
              <p className="mt-6 max-w-md leading-relaxed text-slate-300">
                AI is a capability we bring in when it helps — never the answer by default.
                We identify where AI actually adds value, validate the use case quickly,
                define the requirements and controls, and help the organization move from
                experiment to implementation.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="border-t border-white/15">
                {AI_ITEMS.map((item, i) => (
                  <div
                    key={item}
                    className="flex items-baseline gap-6 border-b border-white/15 py-5"
                  >
                    <span className="font-mono text-[11px] text-slate-500">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="text-base text-slate-200 sm:text-lg">{item}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="environments-section" className="border-b border-line">
        <div className="mx-auto max-w-7xl px-6 py-20 sm:py-28">
          <Reveal>
            <p className="mb-6 font-mono text-[11px] tracking-[0.3em] text-slate-500">
              WHERE WE WORK
            </p>
            <h2 className="max-w-2xl font-serif text-3xl leading-tight tracking-tight text-ink sm:text-4xl">
              Complex operational environments, treated as whole systems.
            </h2>
            <p className="mt-6 max-w-2xl leading-relaxed text-slate-600">
              Our work spans the full surface an outcome depends on. A diagnosis that stops
              at one layer usually finds the symptom's neighbor — not the cause.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-3">
            {ENVIRONMENTS.map((env, i) => (
              <Reveal key={env.name} delay={i * 0.06} className="h-full">
                <div
                  data-testid={`environment-tile-${env.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="flex h-full flex-col bg-canvas p-7"
                >
                  <span className="font-mono text-[10px] tracking-[0.25em] text-slate-400">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-4 text-base font-medium tracking-wide text-ink">{env.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{env.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
